import React, { Component } from 'react';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import ReflexRuleSearchMenu from './reflexRuleSearchMenu';
import ReflexRuleBreadcrumb from './reflexRuleBreadcrumb';
import { ModalStyled } from '../../../../theme/customizedStyleComponents';
import ReflexRuleDetails from './reflexRuleDetails';
import { getReflexRules, deleteReflexRule } from '../../../../services/clinicPortalServices/reflexRuleService';
import {
    getUserRole,
    abortApiCalls,
    releaseApiCalls,
    processCellForClipboard,
    defaultExcelExportParams
} from '../../../../services/common/util';
import { settingConstants } from '../../../../services/common/optionsData';
import { getViewSettings, saveViewDefault } from '../../../../services/clinicPortalServices/userViewSettings';
import toastr from 'toastr';
import EditBtnCellRenderer from './editBtnCellRenderer';
import RuleTypeCellRenderer from './ruleTypeCellRenderer';
import ConfirmModal from '../../../common/confirmModal';
import { ThemeContext } from '../../../../theme/ThemeProvider';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { Modal } from 'react-bootstrap';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

export default class ReflexRuleGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            paginationPageSize: 20,
            quickFilterText: '',
            user_role: getUserRole(),
            showDetails: false,
            showConfirmDelete: false,
            selectedRule: null,
            isNew: false,
            deleteId: null,
            gridName: 'Default',
            columnDefs: [
                {
                    headerName: 'Actions',
                    minWidth: 100,
                    maxWidth: 100,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'editBtnCellRenderer',
                    resizable: true,
                    sortable: false,
                    filter: false
                },
                {
                    headerName: 'Rule Name',
                    field: 'name',
                    minWidth: 200,
                    resizable: true
                },
                {
                    headerName: 'Type',
                    field: 'ruleType',
                    cellRenderer: 'ruleTypeCellRenderer',
                    minWidth: 150,
                    resizable: true
                },
                {
                    headerName: 'Trigger Test',
                    valueGetter: (params) => {
                        if (params.data.triggerTestId && typeof params.data.triggerTestId === 'object') {
                            return params.data.triggerTestId.name || '';
                        }
                        return '';
                    },
                    minWidth: 150,
                    resizable: true
                },
                {
                    headerName: 'Trigger Analyte',
                    valueGetter: (params) => {
                        if (params.data.triggerConditions && params.data.triggerConditions.length > 0) {
                            const firstCondition = params.data.triggerConditions[0];
                            if (firstCondition.analyteId && typeof firstCondition.analyteId === 'object') {
                                return firstCondition.analyteId.name || '';
                            }
                        }
                        return '';
                    },
                    minWidth: 150,
                    resizable: true
                },
                {
                    headerName: 'Condition',
                    valueGetter: (params) => {
                        if (params.data.triggerConditions && params.data.triggerConditions.length > 0) {
                            const cond = params.data.triggerConditions[0];
                            if (cond.operator && cond.value !== undefined) {
                                return `${cond.operator} ${cond.value}${cond.unit ? ' ' + cond.unit : ''}`;
                            }
                        }
                        return '';
                    },
                    minWidth: 120,
                    resizable: true
                },
                {
                    headerName: 'Action',
                    valueGetter: (params) => {
                        if (params.data.reflexActions && params.data.reflexActions.length > 0) {
                            const actionType = params.data.reflexActions[0].actionType;
                            // Convert snake_case to human readable
                            if (actionType) {
                                return actionType.split('_').map(word => 
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                ).join(' ');
                            }
                        }
                        return '';
                    },
                    minWidth: 120,
                    resizable: true
                },
                {
                    headerName: 'Target Tests',
                    valueGetter: (params) => {
                        if (params.data.reflexActions && params.data.reflexActions.length > 0) {
                            const testActions = params.data.reflexActions.filter(ra => ra.actionType === 'add_test' && ra.testId);
                            if (testActions.length > 0) {
                                return testActions.map(ra => {
                                    if (typeof ra.testId === 'object') {
                                        return ra.testId.name || '';
                                    }
                                    return '';
                                }).filter(name => name).join(', ');
                            }
                        }
                        return '';
                    },
                    minWidth: 200,
                    resizable: true
                },
                {
                    headerName: 'Priority',
                    field: 'priority',
                    minWidth: 100,
                    resizable: true
                },
                {
                    headerName: 'Active',
                    field: 'isActive',
                    cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
                    minWidth: 80,
                    resizable: true
                },
                {
                    headerName: 'Created',
                    field: 'createdAt',
                    valueGetter: (params) => {
                        return params.data.createdAt ? moment(params.data.createdAt).format('MM/DD/YYYY HH:mm') : '';
                    },
                    minWidth: 150,
                    resizable: true
                },
                {
                    headerName: 'Last Modified',
                    field: 'updatedAt',
                    valueGetter: (params) => {
                        return params.data.updatedAt ? moment(params.data.updatedAt).format('MM/DD/YYYY HH:mm') : '';
                    },
                    minWidth: 150,
                    resizable: true
                }
            ],
            components: {
                editBtnCellRenderer: EditBtnCellRenderer,
                ruleTypeCellRenderer: RuleTypeCellRenderer
            },
            paginationNumberFormatter: function (params) {
                return '[' + params.value.toLocaleString() + ']';
            },
            defaultColDef: {
                flex: 1,
                filter: true,
                sortable: true
            },
            rowData: [],
            context: { componentParent: this }
        };
    }

    componentDidMount() {
        this.loadGridData();
    }

    onGridReady = (params) => {
        this.gridApi = params.api;
        this.loadGridSchema();
    };

    loadGridData = () => {
        abortApiCalls();
        releaseApiCalls();
        if (this.gridApi) {
            this.gridApi.showLoadingOverlay();
        }

        getReflexRules()
            .then((response) => {
                this.setState({ rowData: response.data });
                if (!response.data.length) {
                    this.gridApi.showNoRowsOverlay();
                } else {
                    this.gridApi.hideOverlay();
                }
            })
            .catch((error) => {
                console.error('Error loading reflex rules:', error);
                toastr.error('Error loading reflex rules');
                this.gridApi.showNoRowsOverlay();
            });
    };

    refreshGrid = () => {
        this.loadGridData();
    };

    onFilterTextChange = (e) => {
        this.setState({ quickFilterText: e.target.value });
    };

    onBtnExport = () => {
        this.gridApi.exportDataAsExcel({
            fileName: 'Reflex_Rules_' + moment().format('YYYY_MM_DD_HH_mm_ss') + '.xlsx',
            sheetName: 'Reflex Rules'
        });
    };

    onPageSizeChanged = () => {
        let value = document.getElementById('page-size').value;
        this.setState({ paginationPageSize: Number(value) });
    };

    loadGridSchema = () => {
        let userId = window.localStorage.getItem('USER_ID');
        let params = {
            user_id: userId,
            type: settingConstants.GRID,
            page: 'REFLEX_RULES'
        };
        getViewSettings(params).then((response) => {
            const columnState =
                response.data &&
                response.data.length > 0 &&
                response.data[0].grid_views.find((item) => {
                    return item.name === settingConstants.DEFAULT_VIEWNAME;
                }).columns;
            if (columnState) {
                this.gridApi.applyColumnState({
                    state: columnState,
                    applyOrder: true
                });
            } else {
                this.gridApi.resetColumnState();
            }

            const pageSize =
                response.data &&
                response.data.length > 0 &&
                response.data[0].grid_views.find((item) => {
                    return item.name === settingConstants.DEFAULT_VIEWNAME;
                }).page_size;
            document.getElementById('page-size').value = pageSize && pageSize > 0 ? pageSize : 20;
            this.onPageSizeChanged();
        });
    };

    saveState = () => {
        let userId = window.localStorage.getItem('USER_ID');
        const columnState = this.gridApi.getColumnState();
        let pageSize = document.getElementById('page-size').value;
        let saveObject = {
            user_id: userId,
            type: settingConstants.GRID,
            page: 'REFLEX_RULES',
            grid_views: [
                {
                    name: settingConstants.DEFAULT_VIEWNAME,
                    page_size: pageSize,
                    columns: columnState
                }
            ]
        };
        saveViewDefault(saveObject).then(() => {
            toastr.success('Saved successfully.');
        });
    };

    resetState = () => {
        this.gridApi.resetColumnState();
    };

    clearFilter = () => {
        this.gridApi.setFilterModel(null);
        this.setState({ quickFilterText: '' });
        document.getElementById('reset-form').value = '';
    };

    handleCreate = () => {
        this.setState({
            showDetails: true,
            selectedRule: null,
            isNew: true
        });
    };

    handleEdit = (rule) => {
        this.setState({
            showDetails: true,
            selectedRule: rule,
            isNew: false
        });
    };

    handleDelete = (id) => {
        this.setState({
            showConfirmDelete: true,
            deleteId: id
        });
    };

    confirmDelete = () => {
        deleteReflexRule(this.state.deleteId)
            .then(() => {
                toastr.success('Rule deleted successfully');
                this.loadGridData();
                this.setState({ showConfirmDelete: false, deleteId: null });
            })
            .catch((error) => {
                console.error('Error deleting rule:', error);
                toastr.error('Error deleting rule');
            });
    };

    handleCloseDetails = () => {
        this.setState({
            showDetails: false,
            selectedRule: null,
            isNew: false
        });
    };

    handleCloseConfirm = () => {
        this.setState({
            showConfirmDelete: false,
            deleteId: null
        });
    };

    render() {
        const canManage = getUserRole() && /superadmin|admin|labtech/.test(getUserRole().toLowerCase());

        return (
            <div className="clinic-contain">
                <ReflexRuleBreadcrumb />
                <ReflexRuleSearchMenu
                    onFilterTextChange={this.onFilterTextChange}
                    clearFilter={this.clearFilter}
                    onPageSizeChanged={this.onPageSizeChanged}
                    pageSize={this.state.paginationPageSize}
                    saveState={this.saveState}
                    resetState={this.resetState}
                    onBtnExport={this.onBtnExport}
                    handleCreate={canManage ? this.handleCreate : null}
                />

                <ModalStyled
                    size="xl"
                    aria-labelledby="contained-modal-title-vcenter"
                    centered
                    show={this.state.showDetails}
                    onHide={this.handleCloseDetails}
                    backdrop="static"
                    dialogClassName="removeTabIndexModal"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>{this.state.isNew ? 'Add Reflex Rule' : 'Edit Reflex Rule'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ReflexRuleDetails
                            rule={this.state.selectedRule}
                            isNew={this.state.isNew}
                            handleClose={this.handleCloseDetails}
                            refreshGrid={this.refreshGrid}
                        />
                    </Modal.Body>
                </ModalStyled>

                <ConfirmModal
                    show={this.state.showConfirmDelete}
                    handleClose={this.handleCloseConfirm}
                    handleConfirm={this.confirmDelete}
                    title="Delete Reflex Rule"
                    message="Are you sure you want to delete this reflex rule? This action cannot be undone."
                />

                <div
                    style={{
                        width: '100%',
                        height: 'calc(100vh - 250px)',
                        padding: '15px'
                    }}
                >
                    <ThemeContext.Consumer>
                        {({ themeName }) => (
                            <div
                                id="myGrid"
                                style={{
                                    height: '100%',
                                    width: '100%'
                                }}
                            >
                                <AgGridReact
                                    columnDefs={this.state.columnDefs}
                                    defaultColDef={this.state.defaultColDef}
                                    onGridReady={this.onGridReady}
                                    rowData={this.state.rowData}
                                    components={this.state.components}
                                    pagination={true}
                                    paginationPageSize={this.state.paginationPageSize}
                                    paginationNumberFormatter={this.state.paginationNumberFormatter}
                                    paginationPageSizeSelector={false}
                                    context={this.state.context}
                                    defaultExcelExportParams={defaultExcelExportParams}
                                    processCellForClipboard={processCellForClipboard}
                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                    quickFilterText={this.state.quickFilterText}
                                />
                            </div>
                        )}
                    </ThemeContext.Consumer>
                </div>
            </div>
        );
    }
}
